import * as React from "react";
import {
    INTERACTION_PROCESS_MTA_CHANGE_DIRECT_DEBIT,
    INTERACTION_PROCESS_NB,
    MIGRATED_RENEWAL,
    MIGRATED_RENEWAL_STATUS_ACCEPTED,
} from "@ferry/journey-services";
import {FalconNavigatorHeader, Footer} from "@ferry/portal-core";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import {MigratedRenewalBanner} from "../../../../../components/banners/MigratedRenewalBanner";
import {MigratedRenewalInvitationBanner} from "../../../../../components/banners/MigratedRenewalInvitationBanner";
import {RenewalConfirmationModal} from "src/components/modals/RenewalConfirmationModal";
import {MigratedRenewalTiles} from "./MigratedRenewalTiles";
import {lazyInject} from "@athena-portal/core";
import {RenewalCancelModal} from "../../../../../components/modals/RenewalCancelModal";
import {RenewalCancelConfirmationModal} from "../../../../../components/modals/RenewalCancelConfirmationModal";
import {StorageSuccessModal} from "../../../../../components/modals/StorageSuccessModal";
import {pushEventToAdobeDataLayerTimeout} from "../../../../../utils/AdobeDataLayerHelper";
import {Types} from "../../../../../Types";
import type {ICustomerPortalService} from "../../../../../services/ICustomerPortalService";

interface IState {
    displayOverlay: boolean;
    match?: any;
    interaction?: any;
    loadingInteraction: boolean;
    showCancelModal: boolean;
    showCancellationSuccessModal: boolean;
    showSuccessModal: boolean;
}

interface IParams {
    interactionId: string;
    [key: string]: string | undefined
}


@authorize
export class MigratedRenewalPagePlain extends React.Component<IWithRouterProps<IParams>, IState> {

    private pluginTemplates = new PluginTemplates();
    private readonly multiPolicyAccount: boolean = false;

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    private pushAdobeDataLayerTimeout: any;

    constructor(props: IWithRouterProps<IParams>) {
        super(props);

        this.state = {
            displayOverlay: true,
            loadingInteraction: true,
            showCancelModal: false,
            showSuccessModal: true,
            showCancellationSuccessModal: false
        };

        this.closeSuccessModal = this.closeSuccessModal.bind(this);
    }

    public componentDidMount(): void {
        this.pluginTemplates.setPageTitle("Dashboard");
        this.loadInteractionByStatus();
    }

    loadInteractionByStatus(): void {
        this.setState({
            loadingInteraction: true,
            displayOverlay: true
        });

        const hasShownModal = sessionStorage.getItem("renewalModalShown");
        this.customerPortalService
            .getInteractionByStatus([MIGRATED_RENEWAL, MIGRATED_RENEWAL_STATUS_ACCEPTED])
            .subscribe((interactionData: any) => {
                if (interactionData) {
                    this.setState({
                        interaction: interactionData,
                        loadingInteraction: false,
                        displayOverlay: false,
                        showSuccessModal: !hasShownModal
                    });
                    if (hasShownModal && window.sessionStorage.getItem("referrerJourney") === INTERACTION_PROCESS_NB
                        && (interactionData.status === MIGRATED_RENEWAL || interactionData.status === MIGRATED_RENEWAL_STATUS_ACCEPTED)) {
                        this.pushAdobeDataLayerTimeout = pushEventToAdobeDataLayerTimeout(2000, "renewed");
                    }
                }
            });
    }

    public componentWillUnmount() {
        clearTimeout(this.pushAdobeDataLayerTimeout);
    }

    public render() {
        const {loadingInteraction, interaction, showCancelModal, showCancellationSuccessModal} = this.state;

        if (loadingInteraction) {
            return <Overlay show={loadingInteraction}/>;
        }

        const isAcceptedRenewal = interaction?.status === MIGRATED_RENEWAL_STATUS_ACCEPTED;
        const referrerJourney = sessionStorage.getItem("referrerJourney");
        const showDirectDebitUpdatedPopup = sessionStorage.getItem("directDebitDetailsUpdated");

        return (
            <>
                <FalconHeader/>
                <div className="page-contents dashboard-page pt-3">
                    <FalconNavigatorHeader
                        back={{
                            link: "/dashboard",
                            hidden: !this.multiPolicyAccount
                        }}
                        title={this.multiPolicyAccount ? "Car policy" : `Hi ${interaction?.policyHolder?.firstName ?? ""},`}
                    />

                    {this.renderBanners()}

                    <MigratedRenewalTiles
                        migratedRenewalStatusAccepted={isAcceptedRenewal}
                        interaction={interaction}
                        onCancelClick={this.openCancelModal}
                    />

                    <RenewalCancelModal
                        title="Cancel renewal"
                        isOpen={showCancelModal}
                        onClose={this.closeCancelModal}
                        onConfirm={this.handleCancelRenewal}
                    />

                    <RenewalCancelConfirmationModal
                        isOpen={showCancellationSuccessModal}
                        expiryDate={interaction?.headerInfo?.expiryDate || "your policy expiry date"}
                        onClose={this.closeSuccessModal}
                    />
                    {(showDirectDebitUpdatedPopup && referrerJourney === INTERACTION_PROCESS_MTA_CHANGE_DIRECT_DEBIT) &&
                        <StorageSuccessModal
                            title="All done!"
                            explanationWording={<p>Your new Direct Debit details have been updated along with your Direct Debit reference.
                                You'll be able to see a new payment plan in your 'Documents' tile shortly.</p>}
                            onClose={this.closeDirectDebitUpdatedModal}
                        />
                    }
                </div>
                <Footer/>
                <Overlay show={loadingInteraction}/>;
            </>
        );
    }

    private renderBanners() {
        const {interaction, showSuccessModal} = this.state;
        const isAcceptedRenewal = interaction?.status === MIGRATED_RENEWAL_STATUS_ACCEPTED;

        return (
            <>
                {isAcceptedRenewal && (
                    <MigratedRenewalBanner interaction={interaction}/>
                )}
                {!isAcceptedRenewal && (
                    <MigratedRenewalInvitationBanner interaction={interaction}/>
                )}
                {showSuccessModal && isAcceptedRenewal && (
                        <RenewalConfirmationModal
                            interaction={interaction}
                            closeRenewalConfirmationModal={this.closeRenewalConfirmationModal}
                        />
                    )}
            </>
        );
    }

    private openCancelModal = (): void => {
        this.setState({showCancelModal: true});
    };

    private closeCancelModal = (): void => {
        this.setState({showCancelModal: false});
    };

    private closeSuccessModal = (): void => {
        this.setState({showCancellationSuccessModal: false});
        this.loadInteractionByStatus();
    };

    private closeDirectDebitUpdatedModal = (): void => {
        sessionStorage.removeItem("referrerJourney");
        sessionStorage.removeItem("directDebitDetailsUpdated");
    }

    private closeRenewalConfirmationModal = (): void => {
        this.setState({showSuccessModal: false});
        sessionStorage.setItem("renewalModalShown", "true");
    };

    private handleCancelRenewal = (): void => {
        const {interaction} = this.state;

        if (!interaction) {
            return;
        }

        this.customerPortalService
            .acceptOrCancelMigratedRenewal(interaction._id, false)
            .subscribe(() => {
                this.setState({
                    showCancelModal: false,
                    showCancellationSuccessModal: true
                });
            });
    };
}

export const MigratedRenewalPage = withRouter<{}, IParams>(MigratedRenewalPagePlain)
